
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { Product, CartItem, ProductVariant } from '@/lib/types';
import { toast } from '@/hooks/use-toast';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import GoogleTranslate from '@/components/layout/GoogleTranslate';
import NoWarrantyAlert from '@/components/shared/NoWarrantyAlert';


interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
  getItemStock: (item: CartItem | Product, partnerId?: string, variantId?: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_EXPIRATION_TIME = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

type StoredCartItem = {
  productId: string;
  variantId?: string;
  quantity: number;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const router = useRouter();

  const [noWarrantyAlert, setNoWarrantyAlert] = useState<{ product: Product, quantity: number, variant?: ProductVariant } | null>(null);

  const getCartKey = useCallback((user: User | null): string => {
    return user ? `cart_v2_${user.uid}` : 'guest_cart_v2';
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const getItemStock = useCallback((item: CartItem | Product, partnerId?: string, variantId?: string): number => {
    const targetVariantId = variantId || (item as CartItem).variantId;

    if (partnerId) {
        if (!item.variants || item.variants.length === 0) return 0;
        
        const variant = item.variants.find(v => v.id === targetVariantId);
        if (!variant || !variant.partnerStock) return 0;

        if (partnerId === 'wolf-express') {
            return Object.values(variant.partnerStock).reduce((sum, stock) => sum + Number(stock || 0), 0);
        }
        return Number(variant.partnerStock[partnerId] || 0);
    }

    if (targetVariantId && item.variants && item.variants.length > 0) {
        const variant = item.variants.find(v => v.id === targetVariantId);
        return variant ? Number(variant.stock || 0) : 0;
    }
    
    if (item.variants && item.variants.length > 0) {
        return item.variants.reduce((acc, v) => acc + Number(v.stock || 0), 0);
    }
    
    return Number((item as Product).stock || 0);
  }, []);

  const proceedAddToCart = useCallback((product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => {
    const itemIdentifier = variant ? variant.id : product.id;
    
    setCartItems(prevItems => {
      const existingIdx = prevItems.findIndex(item => (item.variantId || item.id) === itemIdentifier);
      const currentQuantityInCart = existingIdx > -1 ? prevItems[existingIdx].quantity : 0;

      const promotion = product.promotion;
      if (promotion?.isActive && promotion.maxQuantityPerUser) {
          if (currentQuantityInCart + quantity > promotion.maxQuantityPerUser) {
              toast({
                  variant: "destructive",
                  title: "Limite de Promoção Atingido",
                  description: `Você pode comprar no máximo ${promotion.maxQuantityPerUser} unidades deste item em promoção.`
              });
              return prevItems;
          }
      }

      const stock = Number(getItemStock(product, partnerId, variant?.id));
      if (currentQuantityInCart + quantity > stock) {
          toast({
              variant: "destructive",
              title: "Estoque Insuficiente",
              description: `Você não pode adicionar mais deste item. Máximo em estoque: ${stock}.`
          });
          return prevItems;
      }

      if (existingIdx > -1) {
        const updated = [...prevItems];
        updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + quantity };
        return updated;
      } else {
        const newItem: CartItem = { 
            ...product, 
            variants: product.variants ?? [],
            quantity,
            price: variant ? variant.price : product.price,
            costPrice: variant ? variant.costPrice : product.costPrice,
            imageUrl: variant?.imageUrl || product.imageUrl,
            variantId: variant?.id,
            id: product.id,
            noWarranty: product.noWarranty
        };
        return [...prevItems, newItem];
      }
    });

    toast({
        title: "Produto Adicionado!",
        description: `${product.name}${variant ? ` (${variant.options.name})` : ''} foi adicionado ao seu carrinho.`,
    });
  }, [getItemStock]);

  useEffect(() => {
    const cartKey = getCartKey(currentUser);
    const savedCartData = localStorage.getItem(cartKey);

    if (savedCartData) {
      const { items: storedItems, timestamp } = JSON.parse(savedCartData) as { items: StoredCartItem[], timestamp: number };
      
      if (Date.now() - timestamp > CART_EXPIRATION_TIME) {
        localStorage.removeItem(cartKey);
        setCartItems([]);
        toast({ title: "Seu carrinho expirou", description: "Para garantir os preços e estoque, seu carrinho foi limpo." });
        return;
      }

      const reconstructCart = async (itemsToFetch: StoredCartItem[]) => {
        if (itemsToFetch.length === 0) {
            setCartItems([]);
            return;
        }

        const productIds = Array.from(new Set(itemsToFetch.map(item => item.productId)));
        if (productIds.length === 0) return;
        
        try {
            const fetchedProducts = new Map<string, Product>();
            const productsRef = collection(db, 'products');

            const idChunks: string[][] = [];
            for (let i = 0; i < productIds.length; i += 30) {
                idChunks.push(productIds.slice(i, i + 30));
            }

            const queryPromises = idChunks.map(chunk => 
                getDocs(query(productsRef, where('__name__', 'in', chunk)))
            );
            
            const querySnapshots = await Promise.all(queryPromises);

            querySnapshots.forEach(snapshot => {
                snapshot.forEach(docSnap => {
                    fetchedProducts.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as Product);
                });
            });


            const reconstructedItems: CartItem[] = [];
            for (const item of itemsToFetch) {
                const product = fetchedProducts.get(item.productId);
                if (product) {
                    const variant = item.variantId ? product.variants?.find(v => v.id === item.variantId) : undefined;
                    reconstructedItems.push({
                        ...product,
                        variants: product.variants ?? [],
                        quantity: item.quantity,
                        price: variant ? variant.price : product.price,
                        costPrice: variant ? variant.costPrice : product.costPrice,
                        imageUrl: variant?.imageUrl || product.imageUrl,
                        variantId: item.variantId,
                        id: product.id,
                    });
                }
            }
            setCartItems(reconstructedItems);
        } catch (error) {
            console.error("Error reconstructing cart from Firestore:", error);
            localStorage.removeItem(cartKey);
            setCartItems([]);
        }
      };

      reconstructCart(storedItems);

    } else {
      setCartItems([]);
    }
  }, [currentUser, getCartKey]);


  useEffect(() => {
    const cartKey = getCartKey(currentUser);
    if (cartItems.length > 0) {
      const itemsToStore: StoredCartItem[] = cartItems.map(item => ({
        productId: item.id,
        variantId: item.variantId,
        quantity: item.quantity
      }));
      const cartData = {
        items: itemsToStore,
        timestamp: Date.now()
      };
      localStorage.setItem(cartKey, JSON.stringify(cartData));
    } else {
      localStorage.removeItem(cartKey);
    }
  }, [cartItems, currentUser, getCartKey]);

  useEffect(() => {
    if (currentUser) {
        const pendingItemJSON = localStorage.getItem('pendingCartItem');
        if (pendingItemJSON) {
            const reconstructAndAdd = async () => {
                try {
                    const pendingItem = JSON.parse(pendingItemJSON);
                    const productDoc = await getDoc(doc(db, 'products', pendingItem.productId));
                    if (productDoc.exists()) {
                        const product = { id: productDoc.id, ...productDoc.data() } as Product;
                        const variant = pendingItem.variantId ? product.variants?.find(v => v.id === pendingItem.variantId) : undefined;
                        
                        proceedAddToCart(product, pendingItem.quantity, variant, pendingItem.partnerId);
                    }
                } catch (e) {
                    console.error("Error adding pending item to cart", e);
                } finally {
                    localStorage.removeItem('pendingCartItem');
                }
            };
            reconstructAndAdd();
        }
    }
  }, [currentUser, proceedAddToCart]);


  const checkUserBlocked = async (): Promise<boolean> => {
    const user = auth.currentUser;
    if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data()?.isBlocked) {
            await signOut(auth);
            toast({
                variant: 'destructive',
                title: "Acesso Negado",
                description: "Sua conta está bloqueada. Por favor, contate o suporte.",
                duration: 9000,
            });
            return true;
        }
    }
    return false;
  }

  const addToCart = async (product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => {
    const user = auth.currentUser;
    if (!user) {
        const itemToAdd = {
            productId: product.id,
            quantity,
            variantId: variant?.id,
            partnerId
        };
        localStorage.setItem('pendingCartItem', JSON.stringify(itemToAdd));
        router.push('/login?redirect=/checkout');
        return;
    }
    
    if (await checkUserBlocked()) return;

    try {
        const productRef = doc(db, 'products', product.id);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Produto não foi encontrado em nosso catálogo.' });
            return;
        }
        const freshProduct = { id: productSnap.id, ...productSnap.data() } as Product;
        
        if (freshProduct.noWarranty) {
          setNoWarrantyAlert({ product: freshProduct, quantity, variant });
          return;
        }

        proceedAddToCart(freshProduct, quantity, variant, partnerId);
    } catch (error) {
        console.error("Error fetching fresh product data:", error);
        toast({ variant: 'destructive', title: 'Erro de comunicação', description: 'Não foi possível verificar o estoque. Tente novamente.' });
    }
  };

  const removeFromCart = (productId: string, variantId?: string) => {
    const targetIdentifier = variantId || productId;
    setCartItems(prevItems => {
        const existingIdx = prevItems.findIndex(item => (item.variantId || item.id) === targetIdentifier);
        if (existingIdx === -1) return prevItems;
        const updated = [...prevItems];
        updated.splice(existingIdx, 1);
        return updated;
    });
  };

  const updateQuantity = (productId: string, quantity: number, variantId?: string) => {
    const itemIdentifier = variantId || productId;
    
    if (quantity <= 0) {
      removeFromCart(productId, variantId);
      return;
    } 

    setCartItems(prevItems => {
        const existingIdx = prevItems.findIndex(item => (item.variantId || item.id) === itemIdentifier);
        if (existingIdx === -1) return prevItems;

        const item = prevItems[existingIdx];
        const stock = getItemStock(item);
        let finalQty = quantity;

        if (finalQty > stock) {
            toast({ variant: 'destructive', title: 'Estoque Insuficiente', description: `Máximo de ${stock} unidades em estoque.` });
            finalQty = stock;
        }

        const promotion = item.promotion;
        if (promotion?.isActive && promotion.maxQuantityPerUser && finalQty > promotion.maxQuantityPerUser) {
            toast({
                variant: "destructive",
                title: "Limite de Promoção Atingido",
                description: `Você pode comprar no máximo ${promotion.maxQuantityPerUser} unidades deste item.`
            });
            finalQty = promotion.maxQuantityPerUser;
        }

        const updated = [...prevItems];
        updated[existingIdx] = { ...item, quantity: finalQty };
        return updated;
    });
  };

  const clearCart = () => {
    const cartKey = getCartKey(currentUser);
    localStorage.removeItem(cartKey);
    setCartItems([]);
  };

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getItemStock }}>
      {children}
      <GoogleTranslate />
      <NoWarrantyAlert 
        isOpen={!!noWarrantyAlert}
        onClose={() => setNoWarrantyAlert(null)}
        onConfirm={() => {
            if (noWarrantyAlert) {
                proceedAddToCart(noWarrantyAlert.product, noWarrantyAlert.quantity, noWarrantyAlert.variant);
                setNoWarrantyAlert(null);
            }
        }}
      />
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
